import {
  useModuleBEM,
  useTenantImages,
  useTripsStyles,
} from "@b2bportal/core-themes";
import {
  CoreTripsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { Trans } from "@hopper-b2b/i18n";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import defaultStyles from "./TripsCard.module.scss";

export interface TripsAction {
  label: React.ReactNode;
  action: () => void;
}

export interface TripLeg {
  title: string;
  description: string;
  date: string | React.ReactNode;
}

export interface TripsCardComponentProps extends PlatformComponentProps {
  banner?: JSX.Element;
  status?: JSX.Element;
  title: string;
  onClick?: () => void;
  actions?: TripsAction[];
  confirmationCode: string;
  image?: string;
  imageContent?: React.ReactNode;
  description: string;
  date: string | React.ReactNode;
  constrainImage?: boolean;
  legs?: TripLeg[];
  cardTitle?: string;
}

const COMPONENT_KEY = CoreTripsComponent.TripsCard;

export const TripsCard = ({
  banner,
  className,
  status,
  title,
  onClick,
  confirmationCode,
  image,
  imageContent,
  description,
  date,
  constrainImage = false,
  legs,
  cardTitle,
}: TripsCardComponentProps) => {
  const styles = useTripsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
    constrainImage,
  });
  const images = useTenantImages();
  const imageSrc = image ?? images.roomPlaceholder ?? "";
  const { t } = useI18nContext();
  const contentItems: TripLeg[] =
    legs && legs.length > 0 ? legs : [{ title, description, date }];

  // cardTitle (h2) is present for package trips; leg titles sit one level below
  const LegTitleEl = cardTitle ? "h3" : "h2";

  return (
    <div className={clsx(block, className)}>
      {onClick && (
        <button
          onClick={onClick}
          className={cn("card-button")}
          aria-label={t("trips.viewTripDetailsFor", { title })}
          type="button"
        />
      )}
      {cardTitle && <h2 className={cn("card-title")}>{cardTitle}</h2>}
      <div className={cn("confirmation")}>
        {status != null ? (
          <div className={cn("confirmation-status")}>{status}</div>
        ) : null}
        <div className={cn("confirmation-code")}>
          <Trans
            i18nKey="trips.confirmationCodeLabelAndCode"
            values={{
              code: confirmationCode,
            }}
            components={{
              b: <span className={cn("confirmation-code")} />,
            }}
          />
        </div>
      </div>
      <div className={cn("header")}>
        <div className={cn("image")}>
          {imageContent ? imageContent : <img src={imageSrc} alt={title} />}
        </div>
        <div className={cn("content")}>
          {contentItems.map((item) => (
            <div key={`${item.title}-${item.date}`} className={cn("leg")}>
              <div className={cn("info")}>
                <LegTitleEl className={cn("title")}>{item.title}</LegTitleEl>
                <p className={cn("description")}>{item.description}</p>
              </div>
              <div className={cn("date")}>{item.date}</div>
            </div>
          ))}
        </div>
      </div>
      {banner ? <div className={cn("banner")}>{banner}</div> : null}
    </div>
  );
};
